import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { AxiosError } from 'axios';
import { isString } from 'lodash';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';

export const requestClaimOtp = async ({ email, tin }) => {
  const { data } = await apiClient.post('/business-profiles/claim/request-otp', { email, tin });
  return data;
};

const getErrorMessage = (error, fallback) => {
  if (error instanceof AxiosError) {
    const apiError = error.response?.data?.error;
    if (isString(apiError)) return apiError;
  }
  return isString(error?.message) ? error.message : fallback;
};

const toMutationResult = ({ mutate, isPending, isError, isSuccess }) => ({
  onSubmit: mutate,
  isPending,
  isError,
  isSuccess,
});

const useSnackbar = () => {
  const dispatch = useDispatch();
  return (message, severity) => dispatch(showSnackbar({ message, severity }));
};

export const useRequestClaimOtpHandler = ({ onSuccess } = {}) => {
  const notify = useSnackbar();
  const { snackbarMessages, businessProfiles } = useTranslatedMessages();
  const authOtpMessages = snackbarMessages.auth_otp ?? {};
  const setupSnackbar = businessProfiles?.snackbars ?? {};

  return toMutationResult(
    useMutation({
      mutationFn: requestClaimOtp,
      onSuccess: (data) => {
        notify(data?.message || setupSnackbar.otp_sent_success || authOtpMessages.otp_sent_success, 'success');
        onSuccess?.(data);
      },
      onError: (error) => {
        notify(getErrorMessage(error, setupSnackbar.otp_send_error || authOtpMessages.otp_send_error), 'error');
      },
    }),
  );
};

export const claimBusinessProfileWithOtp = async ({ email, tin, otp }) => {
  const { data } = await apiClient.post('/business-profiles/claim', { email, tin, otp });
  return data;
};

export const useClaimBusinessProfileHandler = ({ onSuccess } = {}) => {
  const notify = useSnackbar();
  const queryClient = useQueryClient();
  const { businessProfiles } = useTranslatedMessages();
  const setupSnackbar = businessProfiles?.snackbars ?? {};

  return toMutationResult(
    useMutation({
      mutationFn: claimBusinessProfileWithOtp,
      onSuccess: (businessProfile) => {
        notify(setupSnackbar.claim_success, 'success');
        if (businessProfile?.profile_id) {
          queryClient.setQueryData(['business_profiles', businessProfile.profile_id], businessProfile);
        }
        queryClient.invalidateQueries({ queryKey: ['business_profiles'] });
        onSuccess?.(businessProfile);
      },
      onError: (error) => {
        notify(getErrorMessage(error, setupSnackbar.claim_error), 'error');
      },
    }),
  );
};
